import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { ArtworkPicker } from '../../components/ui/ArtworkPicker';
import { Card, Chip } from '../../components/ui/Card';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { RepostButton } from '../../components/ui/RepostButton';
import { ApiError, api } from '../../lib/api';
import { type PlayableTrack, usePlayer } from '../../stores/player';
import { Waveform } from '../player/Waveform';
import { CommentThread } from '../comments/CommentThread';
import { ShareEmbed } from './ShareEmbed';
import { ReportButton } from '../moderation/ReportDialog';
import { comments as commentsApi } from '../comments/api';
import { CreditsEditor, type Credit, label } from './CreditsEditor';
import { TrackEditor } from './TrackEditor';
import { useDocumentTitle } from '../../lib/title';

interface Track extends PlayableTrack {
  slug: string;
  description: string | null;
  status: string;
  failure_reason: string | null;
  visibility: 'public' | 'unlisted' | 'private';
  explicit: boolean;
  loudness_lufs: number | null;
  play_count: number;
  like_count: number;
  created_at: string;
  is_owner: boolean;
  favorited: boolean;
  reposted: boolean;
  repost_count: number;
  credits: Credit[];
  genre_id: number | null;
  artwork_small: string | null;
}

export function TrackPage() {
  const { id = '' } = useParams();

  const current = usePlayer((s) => s.track);
  const playing = usePlayer((s) => s.playing);
  const position = usePlayer((s) => s.position);
  const duration = usePlayer((s) => s.duration);
  const play = usePlayer((s) => s.play);
  const toggle = usePlayer((s) => s.toggle);
  const seek = usePlayer((s) => s.seek);

  const [editing, setEditing] = useState(false);
  const [editingCredits, setEditingCredits] = useState(false);

  // The same query key the thread uses, so react-query serves both the pins
  // here and the list below from one request.
  const { data: thread } = useQuery({
    queryKey: ['comments', Number(id)],
    queryFn: () => commentsApi.forTrack(Number(id)),
    enabled: id !== '',
  });


  const { data, error, isPending, refetch } = useQuery({
    queryKey: ['track', id],
    queryFn: () => api.get<Track>(`/tracks/${id}`),
    retry: false,
    /*
     * While it is still transcoding, keep asking.
     *
     * This is the "uploaded but not there yet" state again, and on a track it
     * lasts minutes rather than the second an avatar takes. Polling stops as
     * soon as it resolves either way. Phase 4 replaces it with a `track.ready`
     * event on `private-user.{id}` (§8.1).
     */
    refetchInterval: (query) => {
      const status = query.state.data?.status;

      return status === 'processing' || status === 'pending' ? 3000 : false;
    },
  });

  useDocumentTitle(
    data ? [data.title, data.owner?.display_name ?? data.owner?.username].filter(Boolean).join(' · ') : null,
  );

  if (isPending) {
    return <div className="mx-auto max-w-3xl px-6 py-16 text-soft">Loading…</div>;
  }

  if (error) {
    const missing = error instanceof ApiError && error.status === 404;

    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <Alert tone={missing ? 'info' : 'error'}>
          {missing ? 'That track does not exist, or is not public.' : 'Could not load that track.'}
        </Alert>
      </div>
    );
  }

  const isCurrent = current?.id === data.id;
  const total = isCurrent ? duration || (data.duration_ms ?? 0) / 1000 : (data.duration_ms ?? 0) / 1000;
  const progress = isCurrent && total > 0 ? Math.min(1, position / total) : 0;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-6 py-10">
      <div className="flex flex-wrap items-start gap-5">
        <ArtworkPicker
          subject="tracks"
          subjectId={data.id}
          artwork={data.artwork}
          refetch={() => api.get<Track>(`/tracks/${data.id}`)}
          editable={data.is_owner}
          onUploaded={() => void refetch()}
        />

        <div className="min-w-0 flex-1">
          <h1 className="text-3xl font-bold tracking-tight">{data.title}</h1>

          {data.owner && (
            <Link to={`/${data.owner.username}`} className="meta mt-2 inline-block text-accent hover:underline">
              @{data.owner.username}
            </Link>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {data.visibility !== 'public' && <Chip>{data.visibility}</Chip>}
            {data.status === 'ready' && (
              <span className="meta text-faint">
                {data.play_count.toLocaleString()} plays
              </span>
            )}
          </div>

          {data.description && (
            <p className="mt-4 whitespace-pre-wrap text-[15px] leading-relaxed text-soft">
              {data.description}
            </p>
          )}

          <CreditsList credits={data.credits} />

          {/*
            Sharing, on a public and ready track only.
            Offering an embed code for something still encoding, or for a track
            nobody else can open, hands somebody a snippet that renders "not
            available" on their blog.
          */}
          {data.status === 'ready' && data.visibility === 'public' && (
            <div className="mt-6 border-t border-line pt-5">
              <span className="meta-caps text-faint">Share</span>
              <div className="mt-3">
                <ShareEmbed trackId={data.id} title={data.title} />
              </div>
            </div>
          )}

          {/* Understated, and not offered on your own track - reporting
              yourself is not a thing anybody means to do. */}
          {!data.is_owner && (
            <div className="mt-4">
              <ReportButton subjectType="track" subjectId={data.id} title={data.title} />
            </div>
          )}

          {data.is_owner && (
            <div className="mt-4 -ml-2 flex flex-wrap gap-1">
              <Button variant="ghost" onClick={() => setEditing(true)}>
                Edit track
              </Button>
              <Button variant="ghost" onClick={() => setEditingCredits(true)}>
                Credits
              </Button>
            </div>
          )}
        </div>
      </div>

      {/*
        The four upload states, each said plainly. `processing` is the one
        people find confusing - the file is uploaded and the track is not
        playable yet - and on audio it lasts minutes, so it has to be explicit
        rather than a spinner.
      */}
      {data.status === 'processing' || data.status === 'pending' ? (
        <Alert tone="info">
          Processing. We are building the streaming versions — this usually takes a minute or two, and the page
          will update itself.
        </Alert>
      ) : data.status === 'failed' ? (
        <Alert tone="error">{data.failure_reason ?? 'Processing failed.'}</Alert>
      ) : (
        <Card className="flex flex-col gap-4 p-5">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => {
                if (isCurrent) {
                  toggle();
                } else {
                  void play(toPlayable(data), undefined, 'track');
                }
              }}
            >
              {isCurrent && playing ? 'Pause' : 'Play'}
            </Button>

            <FavoriteButton
              trackId={data.id}
              favorited={data.favorited}
              likeCount={data.like_count}
              showCount
            />

            <RepostButton
              trackId={data.id}
              reposted={data.reposted}
              repostCount={data.repost_count}
              showCount
              // Rewinding is publishing to your followers, so it only applies
              // to something they can hear. The server refuses anything else;
              // this stops the button offering it.
              disabled={data.visibility !== 'public'}
            />

            <Waveform
              url={data.waveform_url}
              progress={progress}
              height={56}
              onSeek={isCurrent ? (fraction) => seek(fraction * total) : undefined}
              className="min-w-0 flex-1"
              // §6.3's waveform pins: comments anchored to a moment, shown
              // where they were left.
              pins={thread?.pins ?? []}
              durationMs={data.duration_ms}
              onPinClick={(pin) => seek(pin.position_ms / 1000)}
            />
          </div>

          {data.loudness_lufs !== null && (
            /*
               Shown because it explains something rather than decorates.
               Everything is normalised to -14 LUFS so the charts are not a
               volume war (§4.2), and an artist seeing their master measured at
               -7 understands why it is not louder than everybody else's.
            */
            <div className="meta text-faint">
              source loudness {data.loudness_lufs} LUFS &middot; normalised to -14 for playback
            </div>
          )}
        </Card>
      )}

      {data.description && (
        <Card className="p-5">
          <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-text-2">{data.description}</p>
        </Card>
      )}

      {data.status === 'ready' && <CommentThread trackId={data.id} />}

      {editingCredits && (
        <CreditsEditor
          trackId={data.id}
          credits={data.credits}
          onClose={() => setEditingCredits(false)}
          onSaved={() => {
            setEditingCredits(false);
            void refetch();
          }}
        />
      )}

      {editing && (
        <TrackEditor
          track={data}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void refetch();
          }}
        />
      )}
    </div>
  );
}

/**
 * The credits block.
 *
 * Grouped by role, because "Producer: A, B" reads as a record sleeve and
 * "A - Producer / B - Producer" reads as a database table.
 *
 * Pending and declined credits are filtered out here as well as on the server.
 * Not defence in depth - the server is the one that decides - but the owner is
 * sent them so their editor can show them, and this is the public block.
 */
function CreditsList({ credits }: { credits: Credit[] }) {
  const shown = credits.filter((credit) => credit.status === undefined || credit.status === 'accepted');

  if (shown.length === 0) return null;

  const groups = new Map<string, Credit[]>();

  for (const credit of shown) {
    groups.set(credit.role, [...(groups.get(credit.role) ?? []), credit]);
  }

  return (
    <dl className="mt-4 flex flex-col gap-1.5 text-sm" data-credits>
      {[...groups.entries()].map(([role, people]) => (
        <div key={role} className="flex flex-wrap gap-x-2">
          <dt className="meta-caps text-faint">{label(role as Credit['role'])}</dt>
          <dd className="flex flex-wrap gap-x-1.5 text-soft">
            {people.map((credit, index) => (
              <span key={credit.id} data-credit={credit.id}>
                {credit.username ? (
                  <Link to={`/${credit.username}`} className="text-ink hover:underline">
                    {credit.name}
                  </Link>
                ) : (
                  <span className="text-ink">{credit.name}</span>
                )}
                {index < people.length - 1 && ','}
              </span>
            ))}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function toPlayable(track: Track): PlayableTrack {
  return {
    id: track.id,
    title: track.title,
    duration_ms: track.duration_ms,
    waveform_url: track.waveform_url,
    artwork: track.artwork,
    artwork_small: track.artwork_small,
    owner: track.owner,
    // Carried through so the dock's heart is drawn correctly the moment
    // playback starts, rather than after a request of its own.
    favorited: track.favorited,
    like_count: track.like_count,
  };
}
